import React, { useState } from 'react'
import {
	Card,
	CardContent,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Settings, Webhook, Plus } from 'lucide-react'
import { motion } from 'framer-motion'
import { WebhookSettings } from './WebhookSettings'
import { WebhookForm } from './WebhookForm'
import { useTranslation } from '@/hooks/useTranslation'

const IntegrationsSettings = () => {
	const { t } = useTranslation()
	const [webhookFormOpen, setWebhookFormOpen] = useState(false)
	const [editingWebhook, setEditingWebhook] = useState(null)

	const handleCreateWebhook = () => {
		setEditingWebhook(null)
		setWebhookFormOpen(true)
	}

	const handleEditWebhook = (webhook: any) => {
		setEditingWebhook(webhook)
		setWebhookFormOpen(true)
	}

	const handleCloseWebhookForm = () => {
		setWebhookFormOpen(false)
		setEditingWebhook(null)
	}

	return (
		<div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Settings className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('integrationsSettings.integrations')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('integrationsSettings.connectExternalServices')}
						</p>
					</div>
				</div>
			</motion.div>

			{/* Webhook Integrations */}
			<WebhookSettings
				onCreateWebhook={handleCreateWebhook}
				onEditWebhook={handleEditWebhook}
			/>

			{/* More Integrations Coming Soon */}
			<Card className="border-dashed border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 relative overflow-hidden">
				<div className="absolute inset-0 bg-grid-small-black/[0.02] [mask-image:radial-gradient(ellipse_at_center,white,transparent)]" />
				<CardContent className="p-8 relative">
					<div className="text-center space-y-6">
						<div className="flex justify-center">
							<div className="relative">
								<div className="p-4 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl shadow-lg">
									<Plus className="w-8 h-8 text-primary" />
								</div>
								<div className="absolute -top-1 -right-1 w-4 h-4 bg-primary/20 rounded-full animate-pulse" />
							</div>
						</div>
						<div className="space-y-3">
							<h3 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
								{t('integrationsSettings.moreIntegrationsComingSoon')}
							</h3>
							<p className="text-muted-foreground max-w-sm mx-auto leading-relaxed">
								{t('integrationsSettings.missingIntegration')}
							</p>
						</div>
						<div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
							<Button
								onClick={() => {
									if (window.feedbask && window.feedbask.openFeature) {
										window.feedbask.openFeature()
									}
								}}
								className="flex items-center gap-2 px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 bg-gradient-to-r from-primary to-primary/90"
								size="lg">
								<Plus className="w-4 h-4" />
								{t('integrationsSettings.submitIntegrationRequest')}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Webhook Form Modal */}
			<WebhookForm
				open={webhookFormOpen}
				onOpenChange={handleCloseWebhookForm}
				webhook={editingWebhook}
			/>
		</div>
	)
}

export default IntegrationsSettings
